import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Printer, Receipt as ReceiptIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, imgUrl, rupiah, tableLabel } from "@/lib/api";
import { PosCartLines, PosMenu } from "@/components/staff/pos/PosMenu";
import { Receipt } from "@/components/staff/pos/Receipt";
import { PosClosing } from "@/components/staff/pos/PosClosing";
import { printHtml } from "@/components/staff/pos/printHtml";

const printReceipt = () => printHtml("pos-receipt");

const input = "w-full px-3 py-2.5 rounded-lg border border-sage-200 bg-white/70 text-sm";
const EMPTY = { order_type: "dine_in", floor: 1, table: 0, name: "", whatsapp: "", promo_consent: false, notes: "" };

const OrderSetup = ({ f, setF, outlets, outlet, setOutlet }) => {
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const type = (t) => (
    <button onClick={() => setF((x) => ({ ...x, order_type: t }))} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold ${f.order_type === t ? "bg-forest text-paper" : "bg-sage-100"}`} data-testid={`pos-type-${t}`}>{t === "dine_in" ? "Makan di tempat" : "Bawa pulang"}</button>
  );
  return (
    <div className="space-y-2.5 py-3 border-t border-sage-200">
      {outlets.length > 1 && (
        <select value={outlet} onChange={(e) => setOutlet(e.target.value)} className={input} data-testid="pos-outlet-select">
          {outlets.map((o) => <option key={o.code} value={o.code}>{o.code} · {o.name}</option>)}
        </select>
      )}
      <div className="flex gap-2">{type("dine_in")}{type("takeaway")}</div>
      {f.order_type === "dine_in" && (
        <div className="grid grid-cols-2 gap-2">
          <select value={f.floor} onChange={(e) => setF((x) => ({ ...x, floor: Number(e.target.value) }))} className={input} data-testid="pos-floor-select">
            {[1, 2].map((n) => <option key={n} value={n}>Lantai {n}</option>)}
          </select>
          <select value={f.table} onChange={(e) => setF((x) => ({ ...x, table: Number(e.target.value) }))} className={input} data-testid="pos-table-select">
            <option value={0}>Pilih meja</option>
            {Array.from({ length: 15 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>Meja {n}</option>)}
          </select>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <input value={f.name} onChange={set("name")} maxLength={60} placeholder="Nama (opsional)" className={input} data-testid="pos-name-input" />
        <input value={f.whatsapp} onChange={set("whatsapp")} maxLength={20} inputMode="tel" placeholder="WhatsApp (opsional)" className={input} data-testid="pos-whatsapp-input" />
      </div>
      {f.whatsapp && <label className="flex items-center gap-2 text-xs text-muted2"><input type="checkbox" checked={f.promo_consent} onChange={set("promo_consent")} data-testid="pos-promo-consent" /> Pelanggan setuju menerima info promo</label>}
      <input value={f.notes} onChange={set("notes")} maxLength={300} placeholder="Catatan pesanan (opsional)" className={input} data-testid="pos-notes-input" />
    </div>
  );
};

const QrisDialog = ({ open, onOpenChange, total, settings, busy, onConfirm }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm" data-testid="pos-qris-dialog">
      <DialogTitle>Bayar dengan QRIS</DialogTitle>
      <img src={imgUrl(settings?.qris_image)} alt="QRIS" className="w-full max-w-[260px] mx-auto rounded-lg border border-sage-200" />
      <div className="text-center"><div className="text-sm text-muted2">Total dibayar</div><div className="text-3xl font-bold text-forest" data-testid="pos-qris-total">{rupiah(total)}</div></div>
      <p className="text-xs text-muted2 text-center">Minta pelanggan scan QRIS, lalu cek notifikasi dana masuk sebesar nominal di atas.</p>
      <button disabled={busy} onClick={onConfirm} className="btn-forest w-full py-3.5 flex items-center justify-center gap-2" data-testid="pos-qris-confirm-button"><CheckCircle2 className="w-5 h-5" /> {busy ? "Menyimpan..." : "Pembayaran QRIS diterima"}</button>
    </DialogContent>
  </Dialog>
);

const ReceiptDialog = ({ order, settings, onClose }) => (
  <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-sm" data-testid="pos-receipt-dialog">
      <DialogTitle>Transaksi berhasil</DialogTitle>
      {order && <div className="bg-white border border-sage-200 rounded-lg p-3 max-h-[50vh] overflow-y-auto"><Receipt order={order} settings={settings} /></div>}
      <div className="flex gap-2">
        <button onClick={() => printReceipt()} className="btn-forest flex-1 py-3 flex items-center justify-center gap-2" data-testid="pos-print-button"><Printer className="w-5 h-5" /> Cetak Struk</button>
        <button onClick={onClose} className="flex-1 py-3 rounded-full bg-sage-100 font-semibold" data-testid="pos-new-order-button">Pesanan baru</button>
      </div>
    </DialogContent>
  </Dialog>
);

const TodayList = ({ list, onReprint }) => (
  <div className="card-paper p-5 mt-6" data-testid="pos-today">
    <h3 className="font-semibold flex items-center gap-2"><ReceiptIcon className="w-4 h-4" /> Transaksi kasir hari ini <span className="text-muted2 font-normal text-sm">· {list.length} · {rupiah(list.reduce((s, o) => s + o.total, 0))}</span></h3>
    <div className="divide-y divide-sage-200 mt-2 text-sm">
      {list.length === 0 && <p className="text-muted2 py-3">Belum ada transaksi.</p>}
      {list.map((o) => (
        <div key={o.id} className="py-2 flex items-center justify-between gap-3" data-testid={`pos-today-${o.id}`}>
          <div className="min-w-0"><span className="font-semibold">{o.code}</span> <span className="text-muted2">· {o.order_type === "takeaway" ? "Bawa pulang" : tableLabel(o.floor, o.table)}{o.customer_name ? ` · ${o.customer_name}` : ""}</span></div>
          <div className="flex items-center gap-3 shrink-0"><span className="font-semibold">{rupiah(o.total)}</span><button onClick={() => onReprint(o)} className="text-forest text-xs font-semibold" data-testid={`pos-reprint-${o.id}`}>Struk</button></div>
        </div>
      ))}
    </div>
  </div>
);

export const PosPanel = () => {
  const [menu, setMenu] = useState([]);
  const [settings, setSettings] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [outlet, setOutlet] = useState("");
  const [lines, setLines] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [payOpen, setPayOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [today, setToday] = useState([]);

  const loadToday = useCallback(() => { api.get("/pos/orders", { params: { outlet } }).then((r) => setToday(r.data)).catch(() => {}); }, [outlet]);
  useEffect(() => {
    api.get("/menu").then((r) => setMenu(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/outlets").then((r) => { setOutlets(r.data); setOutlet((o) => o || r.data[0]?.code || ""); }).catch(() => {});
  }, []);
  useEffect(() => { loadToday(); }, [loadToday]);

  const add = (m) => setLines((ls) => (ls.some((l) => l.id === m.id) ? ls.map((l) => (l.id === m.id ? { ...l, qty: l.qty + 1 } : l)) : [...ls, { id: m.id, name: m.name, price: m.price, qty: 1, note: "" }]));
  const subtotal = lines.reduce((s, l) => s + l.price * l.qty, 0);
  const rate = settings?.tax_rate ?? 10;
  const tax = Math.round((subtotal * rate) / 100);

  const openPay = () => {
    if (!lines.length) return toast.error("Belum ada menu dipilih");
    if (f.order_type === "dine_in" && !f.table) return toast.error("Pilih nomor meja");
    if (f.whatsapp.trim() && !/^(\+?62|0)?8\d{7,12}$/.test(f.whatsapp.replace(/[\s-.]/g, ""))) return toast.error("Nomor WhatsApp tidak valid. Contoh: 0812xxxxxxxx");
    setPayOpen(true);
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pos/orders", { ...f, outlet, payment_confirmed: true, items: lines.map((l) => ({ menu_id: l.id, qty: l.qty, note: l.note })) });
      setPayOpen(false); setReceipt(data); setLines([]); setF(EMPTY); loadToday();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="pos-panel">
      <div className="grid lg:grid-cols-[1fr_380px] gap-6 items-start">
        <PosMenu menu={menu} onAdd={add} />
        <div className="card-paper p-5 lg:sticky lg:top-4" data-testid="pos-cart">
          <h2 className="font-serif font-semibold text-2xl">Kasir</h2>
          <PosCartLines lines={lines} setLines={setLines} />
          <OrderSetup f={f} setF={setF} outlets={outlets} outlet={outlet} setOutlet={setOutlet} />
          <div className="border-t border-sage-200 pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{rupiah(subtotal)}</span></div>
            <div className="flex justify-between"><span>{settings?.tax_name || "PB1"} {rate}%</span><span>{rupiah(tax)}</span></div>
            <div className="flex justify-between font-bold text-xl pt-1"><span>Total</span><span data-testid="pos-total">{rupiah(subtotal + tax)}</span></div>
          </div>
          <button onClick={openPay} className="btn-forest w-full py-4 text-lg mt-4" data-testid="pos-pay-button">Bayar QRIS</button>
        </div>
      </div>
      <TodayList list={today} onReprint={setReceipt} />
      <PosClosing key={`${outlet}-${today.length}`} outlet={outlet} />
      <QrisDialog open={payOpen} onOpenChange={setPayOpen} total={subtotal + tax} settings={settings} busy={busy} onConfirm={confirm} />
      <ReceiptDialog order={receipt} settings={settings} onClose={() => setReceipt(null)} />
    </div>
  );
};
